// Application finder — interface layer. All matching and sizing lives in
// assets/motor-finder.js (window.MotorFinder) so it stays testable without a DOM.
//
// Three things here are deliberate rather than decorative:
//
//   Answers stay on screen as chips you can click to change. Industrial buyers
//   iterate — "what if I went up a size" — and a wizard that makes you press
//   Back four times to find out punishes exactly the behaviour we want.
//
//   The URL carries the whole specification. Someone can send it to their
//   electrician, their boss, or back to us on the phone, and a refresh does not
//   throw the work away. Nobody else in this trade does that.
//
//   The result is a build sheet, not a product list: motor, drive or starter,
//   protection, and a total. That is what makes it a specification a
//   professional can act on rather than a set of search results.
(function () {
  'use strict';

  if (!window.MotorFinder) return;

  var F = window.MotorFinder;

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function money(value) {
    if (value === null || value === undefined) return '';
    return '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  function init(root) {
    var panel = root.querySelector('[data-motor-finder-panel]');
    var app = root.querySelector('[data-motor-finder-app]');
    var fallback = root.querySelector('[data-motor-finder-fallback]');
    if (!panel || !app) return;

    var indexUrl = root.getAttribute('data-index-url');
    var shopName = root.getAttribute('data-shop-name') || '';
    var shopDomain = root.getAttribute('data-shop-domain') || '';
    var phone = root.getAttribute('data-phone') || '';
    var installerUrl = root.getAttribute('data-installer-url') || '';
    var contactUrl = root.getAttribute('data-contact-url') || '/pages/custom-quotes';
    var nameplateUrl = root.getAttribute('data-nameplate-url') || '';

    var rows = null;
    var imageBase = '';
    var indexDate = '';
    var state = { step: 'application' };
    var focusedStep = null;

    if (fallback) fallback.hidden = true;
    app.hidden = false;

    // --- data -----------------------------------------------------------------

    function loadIndex() {
      if (rows) return Promise.resolve(rows);
      return fetch(indexUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('index ' + response.status);
          return response.json();
        })
        .then(function (data) {
          rows = data.products || [];
          imageBase = data.image_base || '';
          indexDate = data.generated || '';
          return rows;
        });
    }

    // Shopify serves resized images off the same URL, so a thumbnail costs a
    // fraction of the full asset.
    // The scheme test has to be the full "https://", not a "http" prefix: five
    // products have filenames that literally begin "https___shop.worldwide..."
    // — Shopify named the uploaded file after the URL it came from. Treating
    // those as absolute drops the CDN prefix and breaks the image.
    function imageUrl(product, width) {
      if (!product.image) return null;
      var absolute = /^https?:\/\//.test(product.image);
      var url = absolute ? product.image : imageBase + product.image;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    // --- steps ----------------------------------------------------------------

    // The subtype question only exists for applications that have one, so the
    // step list is computed rather than fixed.
    function stepList() {
      var steps = ['application'];
      if (state.spec && state.spec.subtypes) steps.push('subtype');
      return steps.concat(['power', 'sizing', 'environment', 'control']);
    }

    var STEP_LABELS = {
      application: 'Driving',
      subtype: 'Type',
      power: 'Shop power',
      sizing: 'Size',
      environment: 'Environment',
      control: 'Control',
    };

    function answerFor(step) {
      switch (step) {
        case 'application':
          return state.spec && state.spec.label;
        case 'subtype':
          return state.subtype && state.subtype.label;
        case 'power':
          return state.power && state.power.label.replace(/\s*\(.*\)$/, '');
        case 'sizing':
          return state.hp ? state.hp + ' HP' + (state.rpm ? ', ' + state.rpm + ' RPM' : '') : null;
        case 'environment':
          return state.environment && state.environment.label;
        case 'control':
          return state.control && state.control.label;
        default:
          return null;
      }
    }

    // Answers you can click to change, rather than reversing through the flow.
    function answerBar() {
      var steps = stepList();
      var answered = steps.filter(function (step) {
        return answerFor(step);
      });
      if (!answered.length) return null;

      var bar = el('div', 'motor-finder__answers');
      answered.forEach(function (step) {
        var chip = el('button', 'motor-finder__answer');
        chip.type = 'button';
        chip.appendChild(el('span', 'motor-finder__answer-label', STEP_LABELS[step]));
        chip.appendChild(el('span', 'motor-finder__answer-value', answerFor(step)));
        chip.setAttribute('aria-label', 'Change ' + STEP_LABELS[step] + ': ' + answerFor(step));
        chip.addEventListener('click', function () {
          state.step = step;
          render();
        });
        bar.appendChild(chip);
      });
      return bar;
    }

    function progress() {
      var steps = stepList();
      var position = steps.indexOf(state.step);
      if (position === -1) return null;
      var wrap = el('p', 'motor-finder__progress');
      wrap.textContent = 'Step ' + (position + 1) + ' of ' + steps.length;
      return wrap;
    }

    // --- URL ------------------------------------------------------------------
    //
    // The specification lives in the address bar: shareable, and a refresh does
    // not lose the work.

    function syncUrl() {
      if (!window.history || !window.history.replaceState) return;
      var params = new URLSearchParams();
      if (state.application) params.set('for', state.application);
      if (state.subtype) params.set('type', state.subtype.id);
      if (state.power) params.set('power', state.power.id);
      if (state.hp) params.set('hp', String(state.hp));
      if (state.rpm) params.set('rpm', String(state.rpm));
      if (state.environment) params.set('where', state.environment.id);
      if (state.control) params.set('run', state.control.id);
      if (state.compare && state.compare.length) params.set('vs', state.compare.join(','));
      var query = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (query ? '?' + query : '')
      );
    }

    function findById(list, id) {
      for (var i = 0; i < list.length; i += 1) {
        if (list[i].id === id) return list[i];
      }
      return null;
    }

    function restoreFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var application = params.get('for');
      if (!application || !F.APPLICATIONS[application]) return false;

      state.application = application;
      state.spec = F.APPLICATIONS[application];
      if (state.spec.subtypes && params.get('type')) {
        state.subtype = findById(state.spec.subtypes, params.get('type'));
      }
      if (params.get('power')) state.power = findById(F.POWER_OPTIONS, params.get('power'));
      if (params.get('hp')) state.hp = Number(params.get('hp'));
      if (params.get('rpm')) state.rpm = Number(params.get('rpm'));
      if (params.get('where')) state.environment = findById(F.ENVIRONMENTS, params.get('where'));
      if (params.get('run')) state.control = findById(F.CONTROL_OPTIONS, params.get('run'));
      if (params.get('vs')) state.compare = params.get('vs').split(',').slice(0, MAX_COMPARE);

      // Resume at the first unanswered step, so a partial link picks up where it
      // left off rather than dumping someone at the start.
      var steps = stepList();
      for (var i = 0; i < steps.length; i += 1) {
        if (!answerFor(steps[i])) {
          state.step = steps[i];
          return true;
        }
      }
      state.step = 'results';
      return true;
    }

    // --- chrome ---------------------------------------------------------------

    function heading(text) {
      var node = el('h2', 'motor-finder__question', text);
      node.setAttribute('tabindex', '-1');
      return node;
    }

    function helper(text) {
      return el('p', 'motor-finder__helper', text);
    }

    function choiceList(options, onPick) {
      var list = el('div', 'motor-finder__choices');
      options.forEach(function (option) {
        var button = el('button', 'motor-finder__choice');
        button.type = 'button';
        button.appendChild(el('span', 'motor-finder__choice-label', option.label));
        if (option.hint) {
          button.appendChild(el('span', 'motor-finder__choice-hint', option.hint));
        }
        button.addEventListener('click', function () {
          onPick(option);
        });
        list.appendChild(button);
      });
      return list;
    }

    function advance() {
      var steps = stepList();
      var position = steps.indexOf(state.step);
      state.step = position === -1 || position === steps.length - 1 ? 'results' : steps[position + 1];
      render();
    }

    function backButton() {
      var steps = stepList();
      var position = steps.indexOf(state.step);
      if (position <= 0) return null;
      var button = el('button', 'motor-finder__back', 'Back');
      button.type = 'button';
      button.addEventListener('click', function () {
        state.step = steps[position - 1];
        render();
      });
      return button;
    }

    // "We do not stock it" is not the same as "we cannot get it". This turns the
    // answers already given into a request the customer can send in one action,
    // rather than making them retype their own specification into a form.
    function sourcingBlock(matchCount) {
      var box = el('div', 'motor-finder__source');

      box.appendChild(
        el(
          'h3',
          'motor-finder__group-label',
          matchCount ? 'Or have us source the exact one' : 'We can still get this'
        )
      );
      box.appendChild(
        el(
          'p',
          'motor-finder__source-lead',
          matchCount
            ? 'Nothing above an exact fit? We are authorised dealers for dozens of ' +
              'manufacturers and source outside our own stock most days — usually in ' +
              'your hands within two to five working days.'
            : 'What we stock is not all we can get. We are authorised dealers for ' +
              'dozens of manufacturers and source outside our own catalogue most ' +
              'days — usually in your hands within two to five working days.'
        )
      );

      var spec = F.specificationText(state, { url: window.location.href });

      var pre = el('pre', 'motor-finder__source-spec', spec);
      pre.setAttribute('tabindex', '0');
      box.appendChild(pre);

      var actions = el('div', 'motor-finder__actions');

      if (phone) {
        var digits = phone.replace(/[^\d+]/g, '');
        var call = el('a', 'motor-finder__cta', 'Call ' + phone);
        call.href = 'tel:' + digits;
        actions.appendChild(call);

        // The ?& before body is the form both iOS and Android accept. Plenty of
        // these people are stood next to the machine with a phone in hand.
        var text = el('a', 'motor-finder__cta motor-finder__cta--quiet', 'Text us this spec');
        text.href = 'sms:' + (digits.charAt(0) === '+' ? digits : '+1' + digits) +
          '?&body=' + encodeURIComponent(spec);
        actions.appendChild(text);
      }

      // Carry the specification into the contact form rather than dropping it at
      // the door. The form reads ?spec= and fills its message box.
      var email = el('a', 'motor-finder__cta motor-finder__cta--quiet', 'Send it to us');
      email.href =
        contactUrl + (contactUrl.indexOf('?') === -1 ? '?' : '&') + 'spec=' + encodeURIComponent(spec);
      actions.appendChild(email);

      var copy = el('button', 'motor-finder__cta motor-finder__cta--quiet', 'Copy specification');
      copy.type = 'button';
      copy.addEventListener('click', function () {
        var done = function () {
          copy.textContent = 'Copied';
          window.setTimeout(function () {
            copy.textContent = 'Copy specification';
          }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(spec).then(done, function () {
            // Clipboard access can be refused; selecting the text still lets
            // them copy it themselves rather than leaving a dead button.
            pre.focus();
            copy.textContent = 'Select it above and copy';
          });
        } else {
          pre.focus();
          copy.textContent = 'Select it above and copy';
        }
      });
      actions.appendChild(copy);

      box.appendChild(actions);
      return box;
    }

    function callout() {
      var box = el('div', 'motor-finder__callout');
      box.appendChild(
        el(
          'p',
          'motor-finder__callout-text',
          'Not sure? Send us the nameplate off your old motor and we will match it.'
        )
      );
      var actions = el('div', 'motor-finder__actions');
      if (phone) {
        var call = el('a', 'motor-finder__cta', 'Call ' + phone);
        call.href = 'tel:' + phone.replace(/[^\d+]/g, '');
        actions.appendChild(call);
      }
      var ask = el('a', 'motor-finder__cta motor-finder__cta--quiet', 'Send us your specs');
      ask.href = contactUrl;
      actions.appendChild(ask);
      box.appendChild(actions);
      return box;
    }

    // --- steps ----------------------------------------------------------------

    function renderApplication() {
      panel.appendChild(heading('What are you driving?'));
      panel.appendChild(
        helper('This decides which questions we ask, not which motors you can have.')
      );

      // Someone standing next to a dead motor with its nameplate in front of
      // them does not want five questions — they want to type what it says. The
      // cross-reference tool already does exactly that job, so this hands them
      // over rather than building a third thing that does the same work.
      if (nameplateUrl) {
        var shortcut = el('a', 'motor-finder__shortcut');
        shortcut.href = nameplateUrl;
        shortcut.appendChild(
          el('span', 'motor-finder__shortcut-label', 'Replacing a motor that failed?')
        );
        shortcut.appendChild(
          el(
            'span',
            'motor-finder__shortcut-hint',
            'Skip the questions — type what the nameplate says and we will match it'
          )
        );
        panel.appendChild(shortcut);
      }

      var options = Object.keys(F.APPLICATIONS).map(function (id) {
        return { id: id, label: F.APPLICATIONS[id].label, hint: F.APPLICATIONS[id].hint };
      });

      panel.appendChild(
        choiceList(options, function (option) {
          state.application = option.id;
          state.spec = F.APPLICATIONS[option.id];
          state.subtype = null;
          state.loading = !rows;
          advance();
          loadIndex()
            .then(function () {
              state.loading = false;
              render();
            })
            .catch(function () {
              state.loading = false;
              state.error = true;
              render();
            });
        })
      );
      panel.appendChild(callout());
    }

    function renderSubtype() {
      panel.appendChild(heading('What kind of ' + state.spec.label.toLowerCase() + '?'));
      panel.appendChild(
        choiceList(state.spec.subtypes, function (option) {
          state.subtype = option;
          advance();
        })
      );
    }

    function renderPower() {
      panel.appendChild(heading('What power do you have in your shop?'));
      panel.appendChild(
        helper(
          'Check the main breaker panel — two hot legs is single-phase, three is three-phase.'
        )
      );
      panel.appendChild(
        choiceList(F.POWER_OPTIONS, function (option) {
          state.power = option;
          advance();
        })
      );
    }

    function labelledSelect(labelText, options, current) {
      var wrap = el('label', 'motor-finder__field');
      wrap.appendChild(el('span', 'motor-finder__field-label', labelText));
      var select = el('select', 'motor-finder__select');
      options.forEach(function (pair) {
        var option = el('option', null, pair[1]);
        option.value = String(pair[0]);
        if (String(pair[0]) === String(current)) option.selected = true;
        select.appendChild(option);
      });
      wrap.appendChild(select);
      return { wrap: wrap, select: select };
    }

    function numberField(labelText, suffix) {
      var wrap = el('label', 'motor-finder__field');
      wrap.appendChild(el('span', 'motor-finder__field-label', labelText));
      var input = el('input', 'motor-finder__input');
      input.type = 'number';
      input.min = '0';
      input.step = 'any';
      wrap.appendChild(input);
      if (suffix) wrap.appendChild(el('span', 'motor-finder__field-suffix', suffix));
      return { wrap: wrap, input: input };
    }

    function renderSizing() {
      var spec = state.spec;
      var defaultRpm = state.rpm || (state.subtype && state.subtype.rpm) || spec.defaultRpm || 1800;

      panel.appendChild(heading('How big does it need to be?'));

      var estimateNote = el('p', 'motor-finder__estimate');
      var hp = labelledSelect(
        'Horsepower',
        F.NEMA_HP.map(function (value) {
          return [value, value + ' HP'];
        }),
        state.hp
      );
      var rpm = labelledSelect(
        'Speed',
        [
          [3600, '3600 RPM (2-pole)'],
          [1800, '1800 RPM (4-pole)'],
          [1200, '1200 RPM (6-pole)'],
          [900, '900 RPM (8-pole)'],
          ['', 'No preference'],
        ],
        defaultRpm
      );

      var form = el('div', 'motor-finder__form');

      if (spec.sizing === 'pump' || spec.sizing === 'fan') {
        var isPump = spec.sizing === 'pump';
        var first = isPump ? numberField('Flow', 'GPM') : numberField('Air flow', 'CFM');
        var second = isPump
          ? numberField('Total head', 'feet')
          : numberField('Static pressure', 'in wg');

        form.appendChild(first.wrap);
        form.appendChild(second.wrap);

        var recalc = function () {
          var estimate = isPump
            ? F.estimatePumpHp(Number(first.input.value), Number(second.input.value))
            : F.estimateFanHp(Number(first.input.value), Number(second.input.value));
          if (!estimate) {
            estimateNote.textContent = '';
            return;
          }
          var sized = F.roundUpToNema(estimate);
          hp.select.value = String(sized);
          if (estimate < 0.25) {
            estimateNote.textContent =
              'That works out well under 0.25 HP, the smallest motor we stock. ' +
              'Worth double-checking those figures — or call and we will size it with you.';
            return;
          }
          estimateNote.textContent =
            'Estimated ' +
            (estimate < 1 ? estimate.toFixed(2) : estimate.toFixed(1)) +
            ' HP, rounded up to the next standard rating: ' +
            sized +
            ' HP. An estimate, not a specification — we verify sizing before anything ships.';
        };
        first.input.addEventListener('input', recalc);
        second.input.addEventListener('input', recalc);
      } else if (spec.sizing === 'guide' && spec.guide) {
        var table = el('table', 'motor-finder__guide');
        table.appendChild(el('caption', null, 'Typical ratings'));
        spec.guide.forEach(function (pair) {
          var tr = el('tr');
          tr.appendChild(el('th', null, pair[0]));
          tr.appendChild(el('td', null, pair[1]));
          table.appendChild(tr);
        });
        panel.appendChild(table);
        panel.appendChild(
          helper(
            'Ranges, not answers — the right size depends on your duty cycle and load. ' +
              'If you are replacing a motor, use the horsepower on its nameplate.'
          )
        );
      }

      form.appendChild(hp.wrap);
      form.appendChild(rpm.wrap);
      panel.appendChild(form);
      panel.appendChild(estimateNote);

      var next = el('button', 'motor-finder__cta', 'Continue');
      next.type = 'button';
      next.addEventListener('click', function () {
        state.hp = Number(hp.select.value);
        state.rpm = rpm.select.value ? Number(rpm.select.value) : null;
        advance();
      });
      panel.appendChild(next);
    }

    function renderEnvironment() {
      panel.appendChild(heading('Where does it live?'));
      panel.appendChild(
        helper(
          'This decides the enclosure — the single biggest factor in how long a ' +
            'motor lasts. Pick by the job, not the code.'
        )
      );
      panel.appendChild(
        choiceList(F.ENVIRONMENTS, function (option) {
          state.environment = option;
          advance();
        })
      );
    }

    function renderControl() {
      panel.appendChild(heading('How does it need to run?'));
      panel.appendChild(
        helper(
          'Separate question from your shop power. Plenty of three-phase shops ' +
            'want a drive for control rather than compatibility.'
        )
      );
      panel.appendChild(
        choiceList(F.CONTROL_OPTIONS, function (option) {
          state.control = option;
          state.step = 'results';
          render();
        })
      );
    }

    // --- results --------------------------------------------------------------

    var KIND_LABEL = { d: 'VFD / Drive', s: 'Starter', r: 'Line Reactor', ss: 'Soft Starter' };
    var MAX_COMPARE = 3;

    function isCompared(handle) {
      return (state.compare || []).indexOf(handle) !== -1;
    }

    function toggleCompare(handle) {
      if (!state.compare) state.compare = [];
      var before = state.compare.length;
      var at = state.compare.indexOf(handle);
      if (at !== -1) state.compare.splice(at, 1);
      else if (state.compare.length < MAX_COMPARE) state.compare.push(handle);

      // Bring the comparison into view the moment it becomes one, and whenever
      // it changes afterwards. Without this the answer appears somewhere above
      // the card you just ticked and you have to go looking for it.
      state.revealCompare = state.compare.length >= 2 && state.compare.length !== before;
      render();
    }

    function revealComparison() {
      if (!state.revealCompare) return;
      state.revealCompare = false;
      var table = panel.querySelector('.motor-finder__compare');
      if (!table) return;
      var heading = table.querySelector('.motor-finder__group-label');
      if (heading && heading.focus) heading.focus({ preventScroll: true });
      if (!table.scrollIntoView) return;
      var reduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      table.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }

    function productByHandle(handle) {
      for (var i = 0; i < rows.length; i += 1) {
        if (rows[i][0] === handle) return F.decode(rows[i]);
      }
      return null;
    }

    function productCard(product, role, comparable) {
      var card = el('article', 'motor-finder__result');

      var media = el('div', 'motor-finder__thumb');
      var src = imageUrl(product, 200);
      if (src) {
        var img = el('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 100;
        img.height = 100;
        media.appendChild(img);
      }
      card.appendChild(media);

      var detail = el('div', 'motor-finder__result-detail');
      detail.appendChild(
        el(
          'span',
          'motor-finder__kind motor-finder__kind--' + (product.kind === 'm' ? 'motor' : 'drive'),
          role || KIND_LABEL[product.kind] || 'Motor'
        )
      );

      var link = el('a', 'motor-finder__result-title', product.title);
      link.href = '/products/' + product.handle;
      detail.appendChild(link);

      var specs = el('dl', 'motor-finder__specs');
      [
        ['HP', product.hp],
        ['RPM', product.rpm],
        ['Volts', product.volts.join('/')],
        ['Enclosure', product.enclosure],
        ['Frame', product.frame],
      ].forEach(function (pair) {
        if (pair[1] === null || pair[1] === undefined || pair[1] === '') return;
        specs.appendChild(el('dt', null, pair[0]));
        specs.appendChild(el('dd', null, String(pair[1])));
      });
      detail.appendChild(specs);

      if (product.price) {
        detail.appendChild(el('p', 'motor-finder__price', money(product.price)));
      }

      if (comparable) {
        var selected = isCompared(product.handle);
        var atLimit = !selected && (state.compare || []).length >= MAX_COMPARE;
        var toggle = el(
          'button',
          'motor-finder__compare-toggle' + (selected ? ' is-selected' : ''),
          selected ? 'Comparing' : 'Compare'
        );
        toggle.type = 'button';
        toggle.disabled = atLimit;
        toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (atLimit) toggle.title = 'Comparing ' + MAX_COMPARE + ' already — remove one first';
        toggle.addEventListener('click', function () {
          toggleCompare(product.handle);
        });
        detail.appendChild(toggle);
      }

      card.appendChild(detail);
      return card;
    }

    function group(labelText, description, results, role, comparable) {
      if (!results.length) return null;
      var wrap = el('div', 'motor-finder__group');
      wrap.appendChild(el('h3', 'motor-finder__group-label', labelText));
      if (description) wrap.appendChild(el('p', 'motor-finder__group-note', description));
      results.forEach(function (result) {
        wrap.appendChild(productCard(result.product, role, comparable));
      });
      return wrap;
    }

    // The comparison. The summary line above the table is the real feature:
    // it says what differs, so nobody has to read thirteen rows to discover two
    // motors are identical apart from speed.
    function comparison() {
      var handles = state.compare || [];
      if (handles.length < 2) return null;

      var products = handles.map(productByHandle).filter(Boolean);
      if (products.length < 2) return null;

      var result = F.compare(products);
      var wrap = el('section', 'motor-finder__compare');
      var title = el('h3', 'motor-finder__group-label', 'Side by side');
      // Focusable so a keyboard or screen-reader user lands on the comparison
      // rather than being scrolled to something they cannot tell they are at.
      title.setAttribute('tabindex', '-1');
      wrap.appendChild(title);

      var summary = el('p', 'motor-finder__compare-summary');
      if (!result.differing.length) {
        summary.textContent =
          'These are identical on every specification we hold. Pick on price or availability.';
      } else {
        var list = result.differing.slice();
        var last = list.pop();
        summary.textContent =
          'They differ on ' + (list.length ? list.join(', ') + ' and ' + last : last) + '.';
      }
      wrap.appendChild(summary);

      var scroller = el('div', 'motor-finder__compare-scroll');
      var table = el('table', 'motor-finder__compare-table');

      var head = el('tr');
      head.appendChild(el('th', 'motor-finder__compare-corner', ''));
      products.forEach(function (product) {
        var cell = el('th');
        var src = imageUrl(product, 200);
        if (src) {
          var thumb = el('div', 'motor-finder__thumb');
          var img = el('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
          cell.appendChild(thumb);
        }
        var link = el('a', 'motor-finder__compare-title', product.title);
        link.href = '/products/' + product.handle;
        cell.appendChild(link);

        var drop = el('button', 'motor-finder__compare-drop', 'Remove');
        drop.type = 'button';
        drop.addEventListener('click', function () {
          toggleCompare(product.handle);
        });
        cell.appendChild(drop);
        head.appendChild(cell);
      });
      table.appendChild(head);

      result.rows.forEach(function (row) {
        var tr = el('tr', row.same ? 'is-same' : 'is-different');
        tr.appendChild(el('th', null, row.label));
        row.values.forEach(function (value) {
          tr.appendChild(el('td', null, value || '—'));
        });
        table.appendChild(tr);
      });

      scroller.appendChild(table);
      wrap.appendChild(scroller);
      wrap.appendChild(
        el(
          'p',
          'motor-finder__compare-legend',
          'Rows that match are dimmed. The ones that stand out are what you are actually choosing between.'
        )
      );
      return wrap;
    }

    // A tray rather than a modal: comparison is something you build up while
    // scrolling the results, so it has to stay out of the way until it is ready.
    function compareTray() {
      var handles = state.compare || [];
      if (!handles.length) return null;

      var tray = el('div', 'motor-finder__tray');
      tray.appendChild(
        el(
          'span',
          'motor-finder__tray-count',
          handles.length + ' selected' + (handles.length < 2 ? ' — pick one more' : '')
        )
      );

      var clear = el('button', 'motor-finder__tray-clear', 'Clear');
      clear.type = 'button';
      clear.addEventListener('click', function () {
        state.compare = [];
        render();
      });
      tray.appendChild(clear);
      return tray;
    }

    // Printed, this has to read as a document from a supplier rather than a
    // screenshot of a web page: who it is from, when, what was specified, and a
    // way back to it. Built into the same DOM and shown only by the print
    // stylesheet, so there is no second render path to keep in sync.
    function printHeader() {
      var header = el('div', 'motor-finder__print-head motor-finder__print-only');

      var masthead = el('div', 'motor-finder__print-masthead');
      masthead.appendChild(el('span', 'motor-finder__print-shop', shopName || 'Motor specification'));
      var contact = el('span', 'motor-finder__print-contact');
      contact.textContent = [phone, shopDomain].filter(Boolean).join('  ·  ');
      masthead.appendChild(contact);
      header.appendChild(masthead);

      header.appendChild(el('h1', 'motor-finder__print-title', 'Motor specification'));

      var meta = el('dl', 'motor-finder__print-meta');
      var pairs = [
        ['Prepared', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })],
        ['Driving', state.spec ? state.spec.label : ''],
        ['Shop power', state.power ? state.power.label : ''],
        ['Rating', state.hp ? state.hp + ' HP' + (state.rpm ? ' at ' + state.rpm + ' RPM' : '') : ''],
        ['Environment', state.environment ? state.environment.label : ''],
        ['Control', state.control ? state.control.label : ''],
      ];
      if (state.subtype) pairs.splice(2, 0, ['Type', state.subtype.label]);

      pairs.forEach(function (pair) {
        if (!pair[1]) return;
        meta.appendChild(el('dt', null, pair[0]));
        meta.appendChild(el('dd', null, pair[1]));
      });
      header.appendChild(meta);
      return header;
    }

    function printFooter() {
      var footer = el('div', 'motor-finder__print-foot motor-finder__print-only');
      footer.appendChild(
        el(
          'p',
          null,
          'Sizing shown is our recommendation and is verified before anything ships. ' +
            'Have a licensed electrician confirm this specification against your ' +
            'service and load before installation.'
        )
      );
      // A printed link cannot be clicked, so the address is set out as text.
      footer.appendChild(
        el('p', 'motor-finder__print-url', 'Return to this specification: ' + window.location.href)
      );
      return footer;
    }

    function renderResults() {
      var required = [];
      if (state.environment && state.environment.require) {
        required = required.concat(state.environment.require);
      }
      if (state.subtype && state.subtype.require) {
        required = required.concat(state.subtype.require);
      }

      var advice = F.systemAdvice(state.power, state.control, state.hp, rows);
      var path = advice.power;

      var criteria = {
        hp: state.hp,
        rpm: state.rpm,
        phase: state.power.phase === 1 ? 3 : state.power.phase,
        volts: state.power.volts,
        enclosure: state.environment && state.environment.enclosure,
        require: required,
        prefer: state.spec.prefer || [],
        needsDrive: advice.needsDrive,
      };

      var relaxedInverterDuty = false;
      var matches;
      if (advice.requireInverterDuty) {
        var strict = {};
        for (var key in criteria) {
          if (Object.prototype.hasOwnProperty.call(criteria, key)) strict[key] = criteria[key];
        }
        strict.require = required.concat(['inv']);
        matches = F.matchProducts(rows, strict);
        if (!matches.length) {
          relaxedInverterDuty = true;
          matches = F.matchProducts(rows, criteria);
        }
      } else {
        matches = F.matchProducts(rows, criteria);
      }
      var shown = matches.slice(0, 6);

      panel.appendChild(printHeader());
      panel.appendChild(heading('Your system'));

      if (path && path.mode !== 'direct') {
        panel.appendChild(el('p', 'motor-finder__banner', path.headline));
      }

      // The build sheet: what it costs together, using the closest motor.
      var pieces = [];
      if (shown.length) pieces.push(shown[0].product);
      advice.drives.slice(0, 1).forEach(function (r) { pieces.push(r.product); });
      advice.starters.slice(0, 1).forEach(function (r) { pieces.push(r.product); });
      advice.reactors.line.forEach(function (r) { pieces.push(r.product); });
      advice.reactors.load.forEach(function (r) { pieces.push(r.product); });

      var total = pieces.reduce(function (sum, p) { return sum + (p.price || 0); }, 0);
      if (total > 0 && pieces.length > 1) {
        var sheet = el('div', 'motor-finder__sheet');
        sheet.appendChild(
          el(
            'p',
            'motor-finder__sheet-note',
            'Everything below works together at ' +
              state.hp +
              ' HP on ' +
              state.power.label.replace(/\s*\(.*\)$/, '') +
              '. Sized and matched by us, not by a filter.'
          )
        );
        var totalRow = el('p', 'motor-finder__sheet-total');
        totalRow.appendChild(el('span', null, pieces.length + ' parts, together'));
        totalRow.appendChild(el('strong', null, money(total)));
        sheet.appendChild(totalRow);
        panel.appendChild(sheet);
      }

      if (advice.requireInverterDuty && !relaxedInverterDuty) {
        panel.appendChild(
          el(
            'p',
            'motor-finder__note',
            'Inverter-duty motors only. A standard motor on a drive overheats at ' +
              'low speed, because the drive stresses insulation the motor was ' +
              'never wound for.'
          )
        );
      } else if (relaxedInverterDuty) {
        panel.appendChild(
          el(
            'p',
            'motor-finder__note motor-finder__note--warn',
            'None of our inverter-duty motors come in this size, so these are ' +
              'standard motors. Run one on a drive and it will not last — call ' +
              'before you order and we will find a rated alternative.'
          )
        );
      }

      // Where the environment is one we cannot serve from stock, say which part
      // is the problem rather than leaving the customer to guess.
      if (state.environment && state.environment.note) {
        panel.appendChild(el('p', 'motor-finder__note', state.environment.note));
      }

      if (!matches.length) {
        var missing = state.environment && state.environment.require
          ? state.environment.label.toLowerCase()
          : null;
        panel.appendChild(
          el(
            'p',
            'motor-finder__empty',
            missing
              ? 'We hold very few motors rated for ' + missing + ' at this size, and ' +
                'none that fit. That is one we source rather than stock.'
              : 'Nothing we hold matches that exactly.'
          )
        );
      } else {
        // The comparison goes above the motors. Ticking a card near the bottom
        // of the list and having the answer appear below it means scrolling past
        // everything you were already looking at.
        var table = comparison();
        if (table) panel.appendChild(table);

        var motorGroup = group(
          matches.length + ' motor' + (matches.length === 1 ? '' : 's') + ' fit',
          matches.length > shown.length
            ? 'Showing the ' + shown.length + ' closest. The first is the best match on size, speed and enclosure. Tick any two or three to compare.'
            : 'Tick any two or three to compare.',
          shown,
          'Motor',
          true
        );
        panel.appendChild(motorGroup);
      }

      var systemGroups = [
        group(
          'The drive',
          advice.reason === 'control'
            ? 'Your service can run the motor without one — this is for control, not compatibility.'
            : 'Bridges your single-phase service to a three-phase motor.',
          advice.drives
        ),
        group(
          'The starter',
          'Switches the motor and carries the overload relay that protects it.',
          advice.starters
        ),
        group(
          'Line-side reactor',
          'Between your supply and the drive. Cuts harmonics fed back into your supply and protects the drive from spikes.',
          advice.reactors.line
        ),
        group(
          'Load-side reactor',
          'Between the drive and the motor. Protects motor windings from the voltage peaks a drive produces — needed beyond about 100 feet.',
          advice.reactors.load
        ),
      ];
      systemGroups.forEach(function (node) {
        if (node) panel.appendChild(node);
      });

      if (advice.needsDrive && !advice.drives.length) {
        panel.appendChild(
          el(
            'p',
            'motor-finder__empty',
            'Nothing in stock matches that drive rating — call and we will source it.'
          )
        );
      }

      // The box the drive has to live in. Two thirds of the drives here are
      // IP20 — a bare chassis that cannot be hung on a wall — so for most
      // customers this is a missing part, not an extra.
      var spec = F.panelSpec(
        state.environment,
        state.hp,
        advice.drives.length ? advice.drives[0].product : null,
        advice.reactors.line.length ? advice.reactors.line[0].product : null
      );
      if (spec) {
        var box = el('div', 'motor-finder__panel-spec');
        box.appendChild(el('h3', 'motor-finder__group-label', 'The enclosure'));

        if (spec.specialist) {
          box.appendChild(el('p', 'motor-finder__panel-headline', spec.headline));
          box.appendChild(el('p', 'motor-finder__group-note', spec.detail));
        } else {
          box.appendChild(
            el(
              'p',
              'motor-finder__panel-headline',
              'This drive is an open chassis. It has to go in an enclosure.'
            )
          );

          var figures = el('dl', 'motor-finder__panel-figures');
          var pairs = [
            ['Enclosure', spec.nema],
            ['Heat to remove', spec.watts + ' W'],
          ];
          if (spec.cfm) pairs.push(['Ventilation', spec.cfm + ' CFM minimum']);
          pairs.forEach(function (pair) {
            figures.appendChild(el('dt', null, pair[0]));
            figures.appendChild(el('dd', null, pair[1]));
          });
          box.appendChild(figures);
          box.appendChild(el('p', 'motor-finder__group-note', spec.why));

          var list = el('ul', 'motor-finder__panel-notes');
          spec.notes.forEach(function (note) {
            list.appendChild(el('li', null, note));
          });
          box.appendChild(list);

          box.appendChild(
            el(
              'p',
              'motor-finder__sourced',
              'We do not list enclosures online yet. Call and we will size the box ' +
                'against your drive and send it with the parts.'
            )
          );
        }
        panel.appendChild(box);
      }

      advice.sourced.forEach(function (note) {
        panel.appendChild(el('p', 'motor-finder__sourced', note));
      });

      if (F.shouldOfferSourcing(matches, state.environment)) {
        panel.appendChild(sourcingBlock(matches.length));
      }

      var tools = el('div', 'motor-finder__tools');
      var print = el('button', 'motor-finder__cta motor-finder__cta--quiet', 'Print this spec');
      print.type = 'button';
      print.addEventListener('click', function () {
        window.print();
      });
      tools.appendChild(print);
      tools.appendChild(
        el(
          'span',
          'motor-finder__tools-note',
          'Or copy the address bar — the link carries this whole specification, so ' +
            'you can send it to your electrician or read it back to us on the phone.'
        )
      );
      panel.appendChild(tools);

      // The question a specification raises but does not answer: who fits it.
      // Nobody can price-shop an installed system, which makes this the part of
      // the offer a national distributor cannot copy.
      if (installerUrl) {
        var installer = el('div', 'motor-finder__installer');
        installer.appendChild(
          el(
            'p',
            null,
            'We have installers across Colorado. Tell us where the job is and we ' +
              'will supply the motor and fit it.'
          )
        );
        var link = el('a', 'motor-finder__cta', 'Ask about installation');
        link.href = installerUrl;
        installer.appendChild(link);
        panel.appendChild(installer);
      }

      panel.appendChild(callout());
      panel.appendChild(printFooter());

      var restart = el('button', 'motor-finder__back', 'Start again');
      restart.type = 'button';
      restart.addEventListener('click', function () {
        state = { step: 'application' };
        render();
      });
      panel.appendChild(restart);

      if (indexDate) {
        panel.appendChild(el('p', 'motor-finder__stamp', 'Catalogue data as of ' + indexDate));
      }
    }

    // --- driver ---------------------------------------------------------------

    function render() {
      panel.innerHTML = '';
      syncUrl();

      if (state.error) {
        panel.appendChild(heading('We could not load the catalogue'));
        panel.appendChild(callout());
        return;
      }

      var bar = answerBar();
      if (bar) panel.appendChild(bar);

      if (state.step !== 'application' && state.step !== 'results') {
        var meter = progress();
        if (meter) panel.appendChild(meter);
      }

      if (state.step !== 'application' && state.loading) {
        panel.appendChild(heading('Loading the catalogue…'));
        return;
      }

      switch (state.step) {
        case 'subtype':
          renderSubtype();
          break;
        case 'power':
          renderPower();
          break;
        case 'sizing':
          renderSizing();
          break;
        case 'environment':
          renderEnvironment();
          break;
        case 'control':
          renderControl();
          break;
        case 'results':
          renderResults();
          break;
        default:
          renderApplication();
      }

      var back = backButton();
      if (back && state.step !== 'results' && state.step !== 'application') panel.appendChild(back);

      // Focus moves when the question changes, not on every redraw. Ticking a
      // compare box re-renders the results, and refocusing the heading each time
      // threw the reader — and the scroll position — back to the top.
      if (state.step !== 'application' && state.step !== focusedStep) {
        focusedStep = state.step;
        var focusTarget = panel.querySelector('.motor-finder__question');
        if (focusTarget) focusTarget.focus();
      }

      var tray = compareTray();
      var existing = root.querySelector('.motor-finder__tray');
      if (existing) existing.remove();
      if (tray && state.step === 'results') root.appendChild(tray);

      revealComparison();
    }

    // A link with a specification in it goes straight to the answer.
    if (restoreFromUrl()) {
      state.loading = true;
      render();
      loadIndex()
        .then(function () {
          state.loading = false;
          render();
        })
        .catch(function () {
          state.loading = false;
          state.error = true;
          render();
        });
    } else {
      render();
    }
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    document.querySelectorAll('[data-motor-finder]').forEach(init);
  });
})();
